import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LANGUAGES from '@/Domain/components/Settings/ai/LANGUAGES';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { MODAL_NAMES } from '@/Domain/store/modal.store';

// Only two-letter codes: every widget language-detection path truncates <html lang>/
// browser/_quickchat('language') to bare ISO-639-1, so compound or longer rows
// (pt-BR, zh-TW, fil) would save but never resolve for a visitor. Bare "pt" is added
// because LANGUAGES only carries the pt-PT/pt-BR DeepL variants.
export const WIDGET_TRANSLATABLE_LANGUAGES: string[] = [
  ...new Set(['pt', ...LANGUAGES.filter((code) => /^[a-z]{2}$/.test(code))]),
];

type Props = {
  used: Set<string>;
  onAdd: (language: string) => void;
  triggerClassName?: string;
};

export const AddLanguagePopover = ({ used, onAdd, triggerClassName }: Props) => {
  const { t } = useTranslation(['website', 'common']);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Portal the dropdown into the translations Sheet: a Sheet's scroll-lock
  // (react-remove-scroll) blocks scrolling inside content portaled to <body>.
  const containerNode = document.getElementById(MODAL_NAMES.WIDGET_TRANSLATIONS_SHEET);

  const options = useMemo(
    () =>
      WIDGET_TRANSLATABLE_LANGUAGES.filter((lng) => !used.has(lng))
        .map((lng) => ({ value: lng, label: t(`languages.${lng}`, { ns: 'common' }) }))
        .filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [used, query, t],
  );

  const handleAdd = (language: string) => {
    onAdd(language);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={triggerClassName}
          data-testid="add-language-button"
        >
          <Plus className="h-4 w-4" />
          {t('text_content_languages.add')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start" containerNode={containerNode}>
        <div className="border-b border-border p-2">
          <Input
            size="sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('text_content_languages.search')}
            startIcon={<Search className="size-4 text-foreground-subtle" />}
            autoFocus
            aria-label={t('text_content_languages.search')}
          />
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {options.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-foreground-subtle">
              {t('text_content_languages.no_results')}
            </p>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleAdd(option.value)}
                className="flex w-full items-center rounded-lg px-2.5 py-1.5 text-left text-sm font-medium text-foreground outline-none hover:bg-accent focus:bg-accent"
                data-testid={`add-language-option-${option.value}`}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
